import type {
  CaptureResult,
  PostHogConfig,
  Properties
} from "posthog-js/dist/module.slim.no-external"

// Stage 1 privacy/persistence decision:
// - PostHog starts automatically on enabled production hosts and uses its
//   normal first-party localStorage+cookie persistence.
// - Existing explicit PostHog opt-outs remain authoritative; this module does
//   not opt visitors back in or reset their consent state.
// - Visitors remain anonymous; this module never calls identify().
// - Allowlisted first-touch attribution is durable, while session-entry
//   attribution remains scoped to the current browser tab.

const CONFIG_SELECTOR = 'meta[name="openreceive-analytics"]'
const PLAUSIBLE_SELECTOR = 'meta[name="openreceive-plausible"]'
const FIRST_TOUCH_STORAGE_KEY = "openreceive.analytics.first_touch.v1"
const SESSION_ENTRY_STORAGE_KEY = "openreceive.analytics.session_entry.v1"
const QA_SESSION_STORAGE_KEY = "openreceive.analytics.qa.v1"
const CHECKOUT_REFERENCE = /^\/checkout\/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\/?$/i
const CHECKOUT_PATH = /^\/checkout(?:\/|$)/i
const SAFE_CAMPAIGN_VALUE = /^[A-Za-z0-9][A-Za-z0-9._~+-]{0,127}$/
const SAFE_HOSTNAME = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/
const POSTHOG_URL_SENTINELS = new Set(["$direct"])
const PLAUSIBLE_SCRIPT_SRC = "https://plausible.io/js/pa-LqJXOY0S1FdUUQdjF82n2.js"
const X_SCRIPT_SRC = "https://static.ads-twitter.com/uwt.js"
const X_CLICK_ID_KEY = "twclid"
const AD_PIXEL_HOST = "openreceive.org"

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id"
] as const

type UtmKey = (typeof UTM_KEYS)[number]
type Environment = "production" | "development"
type AttributionKind = "tagged" | "untagged"
// referring_domain is the external referrer's hostname only (never its path or
// query), so untagged arrivals such as a YouTube description link are visible.
// x_click_id says only whether the landing query carried X's twclid; the
// value itself is never kept. Records stored before November 2, 2026 lack it.
type Attribution = {
  kind: AttributionKind
  utms: Partial<Record<UtmKey, string>>
  referring_domain?: string
  x_click_id?: boolean
}
type AttributionState = { firstTouch: Attribution; sessionEntry: Attribution }

type AnalyticsConfig = {
  environment: Environment
  token: string
  host: string
  xPixelId?: string
}

type AnalyticsDocument = {
  title: string
  referrer?: string
  querySelector: (selector: string) => Element | null
  addEventListener?: (
    type: "click" | "auxclick" | "copy",
    listener: (event: Event) => void,
    capture: boolean
  ) => void
}

type AnalyticsLocation = {
  href: string
  hostname: string
  origin: string
  pathname: string
  search: string
}

type AnalyticsWindow = {
  location: AnalyticsLocation
  localStorage: Pick<Storage, "getItem" | "setItem">
  sessionStorage: Pick<Storage, "getItem" | "setItem">
  addEventListener: (
    type: "pageshow",
    listener: (event: PageTransitionEvent) => void,
    capture: boolean
  ) => void
}

type PlausiblePayload = Record<string, unknown> & { u?: string }

type VendorAdapters = {
  initializePlausible: (transformRequest: (payload: PlausiblePayload) => PlausiblePayload) => void
  initializeX: (pixelId: string, pageLocation: string) => void
  captureXCopy: (pageLocation: string) => void
  captureXDemoOpen: (pageLocation: string) => void
}

type AnalyticsDependencies = {
  document: AnalyticsDocument
  window: AnalyticsWindow
  loadPostHog: () => Promise<PostHogClient>
  vendors?: VendorAdapters
}

// An outbound click may unload the page, so it goes out at once by beacon
// instead of waiting in the SDK's batch.
type CaptureOptions = { send_instantly?: boolean; transport?: "sendBeacon" }
const BEACON: CaptureOptions = { send_instantly: true, transport: "sendBeacon" }

type PostHogClient = {
  init: (token: string, config: Partial<PostHogConfig>) => unknown
  capture: (event: string, properties?: Properties | null, options?: CaptureOptions) => unknown
}

type QueuedFunction = ((...args: unknown[]) => void) & {
  q?: IArguments[]
  queue?: IArguments[]
  exe?: (...args: unknown[]) => void
  version?: string
  init?: (options?: Record<string, unknown>) => void
  o?: Record<string, unknown>
}

type VendorWindow = Window & {
  plausible?: QueuedFunction
  twq?: QueuedFunction
}

const vendorState = new WeakMap<object, {
  plausible: boolean
  x: boolean
  scripts: Set<string>
}>()

const stateFor = (vendorWindow: VendorWindow) => {
  const existing = vendorState.get(vendorWindow)
  if (existing) return existing
  const created = {
    plausible: false,
    x: false,
    scripts: new Set<string>()
  }
  vendorState.set(vendorWindow, created)
  return created
}

const appendScriptOnce = (
  vendorWindow: VendorWindow,
  vendorDocument: Document,
  src: string
): void => {
  const state = stateFor(vendorWindow)
  if (state.scripts.has(src) || vendorDocument.querySelector(`script[src="${src}"]`)) return
  state.scripts.add(src)
  const script = vendorDocument.createElement("script")
  script.async = true
  script.src = src
  const firstScript = vendorDocument.getElementsByTagName("script")[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    vendorDocument.head.appendChild(script)
  }
}

export const createBrowserVendorAdapters = (
  vendorWindow: VendorWindow,
  vendorDocument: Document
): VendorAdapters => ({
  initializePlausible: (transformRequest) => {
    const state = stateFor(vendorWindow)
    if (state.plausible) return
    state.plausible = true
    if (!vendorWindow.plausible) {
      const plausible: QueuedFunction = function () {
        plausible.q ??= []
        plausible.q.push(arguments)
      }
      plausible.init = (options = {}) => { plausible.o = options }
      vendorWindow.plausible = plausible
    }
    vendorWindow.plausible.init ??= (options = {}) => { vendorWindow.plausible!.o = options }
    vendorWindow.plausible.init({ transformRequest })
    appendScriptOnce(vendorWindow, vendorDocument, PLAUSIBLE_SCRIPT_SRC)
  },
  initializeX: (pixelId, pageLocation) => {
    const state = stateFor(vendorWindow)
    if (state.x) return
    state.x = true
    if (!vendorWindow.twq) {
      const twq: QueuedFunction = function () {
        if (twq.exe) twq.exe(...Array.from(arguments))
        else {
          twq.queue ??= []
          twq.queue.push(arguments)
        }
      }
      twq.version = "1.1"
      twq.queue = []
      vendorWindow.twq = twq
    }
    // uwt.js 2.4.11 recognizes these per-pixel controls before config. The
    // first disables its automatic button listener; the second disables dwell
    // and the page-leave events that share the dwell/foreground state.
    vendorWindow.twq("set", "autoConfig", "false", pixelId)
    vendorWindow.twq("set", "autoDwellTracking", "false", pixelId)
    // page_location is a non-event option in the current loader. It also
    // replaces the document-referrer value on requests carrying a referrer.
    vendorWindow.twq("config", pixelId, { page_location: pageLocation })
    appendScriptOnce(vendorWindow, vendorDocument, X_SCRIPT_SRC)
  },
  captureXCopy: (pageLocation) => vendorWindow.twq?.(
    "event",
    "tw-ozspp-rgik2",
    { page_location: pageLocation }
  ),
  captureXDemoOpen: (pageLocation) => vendorWindow.twq?.(
    "event",
    "tw-ozspp-ridv3",
    { page_location: pageLocation }
  )
})

export type CopyCtaLocation =
  | "homepage_framework_grid"
  | "homepage_agent_modal"
  | "integration_hero"
  // The agent tab of a no-code page's setup (WooCommerce).
  | "integration_setup"
  | "integration_next_step"
  | "agent_directions_guide"
  // A processor page's wizard ("OpenReceive + Stripe"): the framework is the
  // one picked there, and the mode rides in neither property.
  | "processor_wizard"

export type FrameworkSelectLocation =
  | "homepage_framework_grid"
  | "site_header"
  | "docs_search"

export type QuickstartCtaLocation =
  | "integration_hero"
  | "integration_copy_caption"
  | "integration_setup"
  | "integration_next_step"
  | "integration_faq"

export type DemoCheckoutLocation = "homepage_demo" | "integration_demo"

// How far an explicitly opened demo checkout got: a payment tile opened, an
// invoice or deposit address shown, the order paid.
export type DemoCheckoutStep = "method_open" | "invoice_shown" | "paid"

// The landing pages that carry the phone-only "finish on your computer" line
// (components/MobileHandoff.tsx), whose share button sends this event.
export type MobileHandoffPage = "integration" | "platform" | "processor" | "home" | "guide"

// Where a site video (helpers/siteVideo.ts) was played.
export type SiteVideoLocation = "homepage" | "integration" | "platform" | "guides"

// How far a played video got: each mark is sent once per page view.
export type SiteVideoProgress = 25 | 50 | 75 | 100

type SiteVideoProperties = {
  video: string
  lang: string
  orientation: "portrait" | "landscape"
  source_location: SiteVideoLocation
}

const SAFE_METHOD = /^[A-Za-z0-9_.-]{1,32}$/
const SAFE_PLATFORM = /^[a-z0-9-]{1,40}$/

type IntentEvent =
  | {
      name: "copy_agent_directions"
      properties: { framework: string; kind: "link" | "directions"; cta_location: CopyCtaLocation }
    }
  | {
      name: "quickstart_click"
      properties: {
        framework: string
        destination_path: string
        cta_location: QuickstartCtaLocation
      }
    }
  | {
      name: "framework_select"
      properties: { framework: string; source_location: FrameworkSelectLocation }
    }
  | {
      name: "demo_checkout_open"
      properties: { source_location: DemoCheckoutLocation }
    }
  | {
      name: "setup_help_view"
      properties: { help_kind: "nwc" | "swap" }
    }
  | {
      name: "demo_checkout_progress"
      properties: { step: DemoCheckoutStep; method: string; source_location: DemoCheckoutLocation }
    }
  | {
      name: "platform_prompt_copy"
      properties: { platform: string }
    }
  | {
      name: "mobile_handoff_share"
      properties: { page_kind: MobileHandoffPage; page_id: string; outcome: "shared" | "dismissed" }
    }
  | {
      name: "video_play"
      properties: SiteVideoProperties
    }
  | {
      name: "video_progress"
      properties: SiteVideoProperties & { percent: SiteVideoProgress }
    }
  | {
      name: "game_play"
      properties: { game: "coin_rush"; source_location: "homepage" }
    }
  | {
      name: "game_over"
      properties: { game: "coin_rush"; source_location: "homepage"; block: number; score_bucket: string }
    }

// Data for the ad pixels only, never sent to PostHog: the pathname a demo
// checkout was opened from, recorded when the click armed it.
type IntentContext = { openedFromPathname?: string }

const defaultDependencies = (): AnalyticsDependencies => ({
  document,
  window,
  vendors: createBrowserVendorAdapters(window, document),
  loadPostHog: async () =>
    (await import("posthog-js/dist/module.slim.no-external")).default as unknown as PostHogClient
})

const safeCampaignValue = (value: string | null): string | null => {
  if (!value) return null
  const trimmed = value.trim()
  return SAFE_CAMPAIGN_VALUE.test(trimmed) ? trimmed : null
}

export const collectAllowedUtms = (search: string): Partial<Record<UtmKey, string>> => {
  const params = new URLSearchParams(search)
  return Object.fromEntries(
    UTM_KEYS.flatMap((key) => {
      const value = safeCampaignValue(params.get(key))
      return value ? [[key, value]] : []
    })
  )
}

// Returns the lowercase hostname of an external http(s) referrer, or null for
// same-origin navigations, missing/opaque referrers and anything unparseable.
export const referringDomain = (referrer: string | undefined, currentOrigin: string): string | null => {
  if (!referrer) return null
  try {
    const url = new URL(referrer)
    if (url.protocol !== "https:" && url.protocol !== "http:") return null
    if (url.origin === currentOrigin) return null
    const hostname = url.hostname.toLowerCase()
    return SAFE_HOSTNAME.test(hostname) ? hostname : null
  } catch {
    return null
  }
}

// An outbound link's destination is reduced exactly like a referrer: the
// external http(s) hostname or nothing (same-site, mailto:, javascript:).
export const externalLinkHost = (href: string, currentOrigin: string): string | null =>
  referringDomain(href, currentOrigin)

// A checkout tile key such as "method:lightning" or "group:USDT" becomes the
// bare method; anything unexpected is reported as "other".
export const demoCheckoutMethod = (pickerKey: string | null | undefined): string => {
  const method = (pickerKey ?? "").split(":").pop() ?? ""
  return SAFE_METHOD.test(method) ? method : "other"
}

export const hasXClickId = (search: string): boolean =>
  Boolean(new URLSearchParams(search).get(X_CLICK_ID_KEY)?.trim())

const attributionFromSearch = (search: string, referrerDomain: string | null = null): Attribution => {
  const utms = collectAllowedUtms(search)
  return {
    kind: Object.keys(utms).length > 0 ? "tagged" : "untagged",
    utms,
    ...(referrerDomain ? { referring_domain: referrerDomain } : {}),
    x_click_id: hasXClickId(search)
  }
}

const validAttribution = (value: unknown): value is Attribution => {
  if (!value || typeof value !== "object") return false
  const candidate = value as { kind?: unknown; utms?: unknown; referring_domain?: unknown; x_click_id?: unknown }
  if (candidate.kind !== "tagged" && candidate.kind !== "untagged") return false
  if (!candidate.utms || typeof candidate.utms !== "object") return false
  if (
    candidate.referring_domain !== undefined &&
    (typeof candidate.referring_domain !== "string" || !SAFE_HOSTNAME.test(candidate.referring_domain))
  ) return false
  if (candidate.x_click_id !== undefined && typeof candidate.x_click_id !== "boolean") return false

  return Object.entries(candidate.utms).every(([key, value]) =>
    UTM_KEYS.includes(key as UtmKey) &&
    typeof value === "string" &&
    SAFE_CAMPAIGN_VALUE.test(value)
  )
}

type AnalyticsStorage = Pick<Storage, "getItem" | "setItem">

const readStoredAttribution = (storage: AnalyticsStorage, key: string): Attribution | null => {
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return validAttribution(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const resolveAttribution = (
  search: string,
  storedFirstTouch: Attribution | null,
  storedSessionEntry: Attribution | null,
  referrerDomain: string | null = null
): AttributionState => {
  const incoming = attributionFromSearch(search, referrerDomain)

  return {
    firstTouch: storedFirstTouch ?? incoming,
    // A newly tagged entry, an X ad click (twclid, which website cards can
    // carry without UTMs or a referrer), or an arrival from a different
    // external site starts a new attributable visit in this tab. An ordinary
    // internal navigation (same-origin or no referrer) keeps the existing
    // entry, and so does a reload after SPA navigation, which repeats the
    // arrival's referrer without its UTMs.
    sessionEntry:
      incoming.kind === "tagged" ||
      incoming.x_click_id ||
      (incoming.referring_domain && incoming.referring_domain !== storedSessionEntry?.referring_domain)
        ? incoming
        : storedSessionEntry ?? incoming
  }
}

const persistAttribution = (
  storage: AnalyticsStorage,
  key: string,
  attribution: Attribution
): void => {
  try {
    storage.setItem(key, JSON.stringify(attribution))
  } catch {
    // Storage denial must not affect rendering or controls. PostHog itself
    // falls back independently; this page still captures without attribution.
  }
}

const prefixedUtms = (
  prefix: "first_touch" | "session_entry",
  attribution: Attribution
): Properties => ({
  ...Object.fromEntries(
    Object.entries(attribution.utms).map(([key, value]) => [`${prefix}_${key}`, value])
  ),
  ...(attribution.referring_domain ? { [`${prefix}_referring_domain`]: attribution.referring_domain } : {}),
  ...(typeof attribution.x_click_id === "boolean" ? { [`${prefix}_x_click_id`]: attribution.x_click_id } : {})
})

const attributionProperties = (
  currentSearch: string,
  attribution: AttributionState
): Properties => {
  const current = attributionFromSearch(currentSearch)
  return {
    current_attribution: current.kind,
    ...current.utms,
    first_touch_attribution: attribution.firstTouch.kind,
    ...prefixedUtms("first_touch", attribution.firstTouch),
    session_entry_attribution: attribution.sessionEntry.kind,
    ...prefixedUtms("session_entry", attribution.sessionEntry)
  }
}

export const sanitizePathname = (pathname: string): string => {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`
  if (CHECKOUT_REFERENCE.test(path)) return "/checkout/:reference"
  return path.replace(/\/{2,}/g, "/")
}

export const sanitizeUrl = (value: string, currentOrigin: string): string | null => {
  if (POSTHOG_URL_SENTINELS.has(value)) return value
  try {
    const url = new URL(value, currentOrigin)
    const path = url.origin === currentOrigin ? sanitizePathname(url.pathname) : ""
    return `${url.origin}${path}`
  } catch {
    return null
  }
}

export const sanitizePlausibleUrl = (value: string, currentOrigin: string): string => {
  try {
    const url = new URL(value, currentOrigin)
    const clean = new URL(url.origin)
    clean.pathname = url.origin === currentOrigin ? sanitizePathname(url.pathname) : "/"
    UTM_KEYS.forEach((key) => {
      const campaignValue = safeCampaignValue(url.searchParams.get(key))
      if (campaignValue) clean.searchParams.set(key, campaignValue)
    })
    return clean.toString()
  } catch {
    return currentOrigin
  }
}

export const sanitizePlausibleRequest = (
  payload: PlausiblePayload,
  currentOrigin: string,
  currentUrl: string
): PlausiblePayload => ({
  ...payload,
  u: sanitizePlausibleUrl(typeof payload.u === "string" ? payload.u : currentUrl, currentOrigin)
})

const sanitizePropertyMap = (properties: Properties, currentOrigin: string): Properties => {
  const sanitized: Properties = {}
  Object.entries(properties).forEach(([key, value]) => {
    if (typeof value !== "string") {
      sanitized[key] = value
      return
    }

    const normalizedKey = key.toLowerCase()
    if (normalizedKey.includes("url") || normalizedKey.includes("referrer")) {
      const clean = sanitizeUrl(value, currentOrigin)
      if (clean) sanitized[key] = clean
      return
    }
    if (
      normalizedKey.includes("pathname") ||
      normalizedKey === "source_page" ||
      normalizedKey.endsWith("_path")
    ) {
      sanitized[key] = sanitizePathname(value)
      return
    }

    sanitized[key] = value
  })
  return sanitized
}

export const sanitizeCapture = (
  capture: CaptureResult | null,
  currentOrigin: string
): CaptureResult | null => {
  if (!capture) return null
  return {
    ...capture,
    properties: sanitizePropertyMap(capture.properties, currentOrigin),
    ...(capture.$set ? { $set: sanitizePropertyMap(capture.$set, currentOrigin) } : {}),
    ...(capture.$set_once ? { $set_once: sanitizePropertyMap(capture.$set_once, currentOrigin) } : {})
  }
}

export const runAfterSuccessfulAction = async (
  action: () => Promise<unknown>,
  onSuccess: () => void
): Promise<boolean> => {
  try {
    await action()
    onSuccess()
    return true
  } catch {
    return false
  }
}

const configFromDocument = (analyticsDocument: AnalyticsDocument): AnalyticsConfig | null => {
  const element = analyticsDocument.querySelector(CONFIG_SELECTOR)
  const dataset = (element as HTMLElement | null)?.dataset
  if (!dataset) return null

  const environment = dataset.environment
  const token = dataset.posthogToken
  const host = dataset.posthogHost
  if ((environment !== "production" && environment !== "development") || !token || !host) return null
  return {
    environment,
    token,
    host,
    xPixelId: dataset.xPixelId
  }
}

const plausibleEnabled = (analyticsDocument: AnalyticsDocument): boolean =>
  Boolean(analyticsDocument.querySelector(PLAUSIBLE_SELECTOR))

const qaVisitIsSuppressed = (search: string, storage: AnalyticsStorage): boolean => {
  const source = new URLSearchParams(search).get("utm_source")?.trim().toLowerCase()
  if (source === "qa") {
    try {
      storage.setItem(QA_SESSION_STORAGE_KEY, "true")
    } catch {
      // The runtime keeps the decision for this document even without storage.
    }
    return true
  }
  try {
    return storage.getItem(QA_SESSION_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

const sanitizedCurrentUrl = (location: AnalyticsLocation): string =>
  `${location.origin}${sanitizePathname(location.pathname)}`

const adMeasurementAllowedOnPath = (pathname: string): boolean =>
  !CHECKOUT_PATH.test(pathname)

export const createAnalyticsRuntime = (dependencies: AnalyticsDependencies = defaultDependencies()) => {
  const vendors = dependencies.vendors
  let config: AnalyticsConfig | null = null
  let attribution: AttributionState | null = null
  let client: PostHogClient | null = null
  let started = false
  let lastPageviewPath: string | null = null
  const pending: Array<{ name: string; properties: Properties; options?: CaptureOptions }> = []
  const copyConversions: Array<() => void> = []
  // Only X takes demo_checkout_open as a conversion.
  const demoOpenConversions: Array<(pageLocation: string) => void> = []

  const startAdVendor = (
    initialize: () => void,
    captureCopy: () => void,
    captureDemoOpen?: (pageLocation: string) => void
  ): void => {
    try {
      initialize()
      copyConversions.push(captureCopy)
      if (captureDemoOpen) demoOpenConversions.push(captureDemoOpen)
    } catch {
      // One blocked or broken vendor must not prevent another from starting.
    }
  }

  // An SDK that re-measures a bfcache restore would read the live URL, which
  // after SPA navigation can be /checkout/<reference>. This capture listener
  // is registered before any SDK loads, so it runs ahead of theirs.
  const blockCheckoutBfcacheRestore = (): void => {
    try {
      dependencies.window.addEventListener("pageshow", (event) => {
        if (event.persisted && !adMeasurementAllowedOnPath(dependencies.window.location.pathname)) {
          event.stopImmediatePropagation()
        }
      }, true)
    } catch {
      // Without the listener the pixels still never receive an explicit call here.
    }
  }

  const baseProperties = (): Properties => {
    if (!config || !attribution) return {}
    return {
      environment: config.environment,
      source_page: sanitizePathname(dependencies.window.location.pathname),
      ...attributionProperties(dependencies.window.location.search, attribution)
    }
  }

  const send = (name: string, properties: Properties = {}, options?: CaptureOptions): void => {
    if (!config || !attribution) return
    const event = { name, properties: { ...baseProperties(), ...properties }, options }
    if (!client) {
      pending.push(event)
      return
    }
    try {
      if (options) client.capture(event.name, event.properties, options)
      else client.capture(event.name, event.properties)
    } catch {
      // Analytics must never break a user action.
    }
  }

  // Two signals of informed interest that need no component of their own:
  // following a link off the site (hostname only), and copying from a code
  // block by hand (never the copied text). Neither is an ad conversion.
  const observeInteractions = (): void => {
    const target = dependencies.document
    if (typeof target.addEventListener !== "function") return
    const onLinkActivate = (event: Event): void => {
      try {
        if (event.type === "auxclick" && (event as MouseEvent).button !== 1) return
        const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null
        if (!anchor) return
        const host = externalLinkHost(anchor.href, dependencies.window.location.origin)
        if (host) send("outbound_click", { destination_host: host }, BEACON)
      } catch {
        // Measuring a click must never stop the navigation it measures.
      }
    }
    const onCopy = (event: Event): void => {
      try {
        if (!adMeasurementAllowedOnPath(dependencies.window.location.pathname)) return
        const node = event.target as Node | null
        const element = node?.nodeType === 1 ? (node as Element) : node?.parentElement
        if (element?.closest?.("pre, code")) send("code_copy", { copy_target: "code_block" })
      } catch {
        // Copying must keep working whatever happens here.
      }
    }
    try {
      target.addEventListener("click", onLinkActivate, true)
      target.addEventListener("auxclick", onLinkActivate, true)
      target.addEventListener("copy", onCopy, true)
    } catch {
      // Without the listeners the page simply records fewer signals.
    }
  }

  const capturePageview = (): void => {
    const page = sanitizePathname(dependencies.window.location.pathname)
    if (page === lastPageviewPath) return
    lastPageviewPath = page
    send("$pageview", { $title: dependencies.document.title })
  }

  const postHogConfig = (): Partial<PostHogConfig> => ({
    api_host: config?.host,
    ui_host: "https://us.posthog.com",
    defaults: "2026-05-30",
    person_profiles: "identified_only",
    persistence: "localStorage+cookie",
    persistence_name: "openreceive_stage1",
    cross_subdomain_cookie: false,
    autocapture: false,
    rageclick: false,
    capture_pageview: false,
    // $pageleave carries the last page's time on page and scroll depth
    // ($prev_pageview_duration / _max_scroll_percentage), which no later
    // $pageview would; its URL goes through get_current_url like every event.
    capture_pageleave: true,
    disable_session_recording: true,
    capture_performance: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    disable_scroll_properties: false,
    disable_surveys: true,
    disable_surveys_automatic_display: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_web_experiments: true,
    disable_external_dependency_loading: true,
    enable_recording_console_log: false,
    advanced_disable_flags: true,
    advanced_disable_feature_flags: true,
    save_campaign_params: false,
    // Referrers drive PostHog channel attribution (Direct / Organic Search /
    // Referral); before_send reduces each one to its bare origin.
    save_referrer: true,
    respect_dnt: true,
    opt_in_site_apps: false,
    disableDeviceModel: true,
    mask_personal_data_properties: true,
    custom_personal_data_properties: ["email", "token", "secret", "nwc", "lsc"],
    get_current_url: () => sanitizedCurrentUrl(dependencies.window.location),
    before_send: (capture) => sanitizeCapture(capture, dependencies.window.location.origin)
  })

  const start = (): void => {
    if (started) return
    started = true
    const documentConfig = configFromDocument(dependencies.document)
    config = documentConfig
    const productionAdPixelContext =
      documentConfig?.environment === "production" &&
      dependencies.window.location.hostname === AD_PIXEL_HOST
    // Record the tab marker even when this document is a checkout, where the
    // pixels are skipped for the separate URL-data boundary below.
    const adPixelsSuppressed = productionAdPixelContext
      ? qaVisitIsSuppressed(dependencies.window.location.search, dependencies.window.sessionStorage)
      : false

    if (
      vendors &&
      productionAdPixelContext &&
      adMeasurementAllowedOnPath(dependencies.window.location.pathname) &&
      !adPixelsSuppressed
    ) {
      const location = dependencies.window.location
      const xPixelId = documentConfig?.xPixelId
      if (xPixelId) startAdVendor(
        () => vendors.initializeX(xPixelId, sanitizedCurrentUrl(location)),
        () => vendors.captureXCopy(sanitizedCurrentUrl(location)),
        (pageLocation) => vendors.captureXDemoOpen(pageLocation)
      )
      if (copyConversions.length > 0) blockCheckoutBfcacheRestore()
    }

    if (plausibleEnabled(dependencies.document) && vendors) {
      try {
        vendors.initializePlausible((payload) => sanitizePlausibleRequest(
          payload,
          dependencies.window.location.origin,
          dependencies.window.location.href
        ))
      } catch {
        // Plausible is independent of the app and the other analytics vendors.
      }
    }

    if (!config) return

    attribution = resolveAttribution(
      dependencies.window.location.search,
      readStoredAttribution(dependencies.window.localStorage, FIRST_TOUCH_STORAGE_KEY),
      readStoredAttribution(dependencies.window.sessionStorage, SESSION_ENTRY_STORAGE_KEY),
      referringDomain(dependencies.document.referrer, dependencies.window.location.origin)
    )
    persistAttribution(
      dependencies.window.localStorage,
      FIRST_TOUCH_STORAGE_KEY,
      attribution.firstTouch
    )
    persistAttribution(
      dependencies.window.sessionStorage,
      SESSION_ENTRY_STORAGE_KEY,
      attribution.sessionEntry
    )
    capturePageview()

    const helpKind =
      dependencies.window.location.pathname === "/get_a_nwc_code_to_receive_payments"
        ? "nwc"
        : dependencies.window.location.pathname === "/set_up_swap_provider"
          ? "swap"
          : null
    if (helpKind) send("setup_help_view", { help_kind: helpKind })
    observeInteractions()

    void dependencies.loadPostHog().then((loadedClient) => {
      try {
        loadedClient.init(config!.token, postHogConfig())
        client = loadedClient
        pending.splice(0).forEach((event) => {
          if (event.options) client?.capture(event.name, event.properties, event.options)
          else client?.capture(event.name, event.properties)
        })
      } catch {
        // SDK load/init/capture failure is intentionally isolated from the app.
      }
    }).catch(() => {
      // Blocking the SDK chunk or analytics host leaves the site functional.
    })
  }

  // The shop pushes /checkout/<reference> before it shows the payment screen,
  // so demo_checkout_open normally fires on the checkout URL. X is sent the
  // page the click came from instead, and nothing when that is unknown or was
  // itself a checkout path.
  const captureDemoOpenConversion = (openedFromPathname: string | undefined): void => {
    if (demoOpenConversions.length === 0 || !openedFromPathname?.startsWith("/")) return
    const path = sanitizePathname(openedFromPathname)
    if (!adMeasurementAllowedOnPath(path)) return
    const pageLocation = `${dependencies.window.location.origin}${path}`
    demoOpenConversions.forEach((captureDemoOpen) => {
      try {
        captureDemoOpen(pageLocation)
      } catch {
        // A vendor failure must not break the demo checkout.
      }
    })
  }

  const captureIntent = (event: IntentEvent, context: IntentContext = {}): void => {
    send(event.name, event.properties)
    if (event.name === "demo_checkout_open") {
      captureDemoOpenConversion(context.openedFromPathname)
      return
    }
    if (event.name !== "copy_agent_directions") return
    // A pixel initialized on a public SPA route may still be resident after
    // the shop pushes /checkout/<reference>. No vendor gets a conversion
    // from that path, whose reference must never become SDK-owned URL data.
    if (!adMeasurementAllowedOnPath(dependencies.window.location.pathname)) return
    copyConversions.forEach((captureCopy) => {
      try {
        captureCopy()
      } catch {
        // A vendor failure must not break copying or another vendor.
      }
    })
  }

  return { start, captureIntent, capturePageview }
}

let defaultAnalytics: ReturnType<typeof createAnalyticsRuntime> | null = null
const analytics = () => {
  defaultAnalytics ??= createAnalyticsRuntime()
  return defaultAnalytics
}

export const startAnalytics = (): void => analytics().start()
export const commitAnalyticsPageview = (): void => analytics().capturePageview()

export const captureCopyAgentDirections = (properties: {
  framework: string
  kind: "link" | "directions"
  ctaLocation: CopyCtaLocation
}): void => analytics().captureIntent({
  name: "copy_agent_directions",
  properties: {
    framework: properties.framework,
    kind: properties.kind,
    cta_location: properties.ctaLocation
  }
})

export const captureQuickstartClick = (properties: {
  framework: string
  destinationPath: string
  ctaLocation: QuickstartCtaLocation
}): void => analytics().captureIntent({
  name: "quickstart_click",
  properties: {
    framework: properties.framework,
    destination_path: sanitizePathname(new URL(properties.destinationPath, "https://openreceive.org").pathname),
    cta_location: properties.ctaLocation
  }
})

export const captureFrameworkSelect = (
  framework: string,
  sourceLocation: FrameworkSelectLocation
): void => analytics().captureIntent({
  name: "framework_select",
  properties: { framework, source_location: sourceLocation }
})

export const captureDemoCheckoutOpen = (
  sourceLocation: DemoCheckoutLocation,
  openedFromPathname?: string
): void =>
  analytics().captureIntent({
    name: "demo_checkout_open",
    properties: { source_location: sourceLocation }
  }, { openedFromPathname })

export const captureDemoCheckoutProgress = (
  step: DemoCheckoutStep,
  pickerKey: string | null | undefined,
  sourceLocation: DemoCheckoutLocation
): void => analytics().captureIntent({
  name: "demo_checkout_progress",
  properties: { step, method: demoCheckoutMethod(pickerKey), source_location: sourceLocation }
})

export const capturePlatformPromptCopy = (platform: string): void =>
  analytics().captureIntent({
    name: "platform_prompt_copy",
    properties: { platform: SAFE_PLATFORM.test(platform) ? platform : "other" }
  })

// "dismissed" is the share sheet closed without sending: the visitor still
// asked for the page on another device, which is the signal worth counting.
export const captureMobileHandoffShare = (
  pageKind: MobileHandoffPage,
  pageId: string,
  outcome: "shared" | "dismissed"
): void =>
  analytics().captureIntent({
    name: "mobile_handoff_share",
    properties: { page_kind: pageKind, page_id: SAFE_PLATFORM.test(pageId) ? pageId : "other", outcome }
  })

export const captureVideoPlay = (properties: SiteVideoProperties): void =>
  analytics().captureIntent({ name: "video_play", properties })

export const captureVideoProgress = (properties: SiteVideoProperties, percent: SiteVideoProgress): void =>
  analytics().captureIntent({ name: "video_progress", properties: { ...properties, percent } })

// Coin Rush, the home page game (app/javascript/src/game). The score goes in
// as a bucket, never the exact number: the event is about engagement.
const SCORE_BUCKETS = [100, 500, 1000, 2500, 5000, 10000]

export const gameScoreBucket = (score: number): string => {
  const top = SCORE_BUCKETS.find((limit) => score < limit)
  return top === undefined ? "10000+" : `<${top}`
}

export const captureGamePlay = (): void =>
  analytics().captureIntent({ name: "game_play", properties: { game: "coin_rush", source_location: "homepage" } })

export const captureGameOver = (block: number, score: number): void =>
  analytics().captureIntent({
    name: "game_over",
    properties: {
      game: "coin_rush",
      source_location: "homepage",
      block: Math.max(1, Math.min(99, Math.floor(block))),
      score_bucket: gameScoreBucket(score)
    }
  })
